import { useState } from "react";
import { ServiceCard } from "./ServiceCard";
import { Filter, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import MultiServiceScheduler from "@/components/booking/MultiServiceScheduler";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

interface ServiceGridProps {
  companies?: any[];
  onCompanySelect: (companyId: string) => void;
  searchTerm: string;
}

export const ServiceGrid = ({ companies = [], onCompanySelect, searchTerm }: ServiceGridProps) => {
  const [openPlanner, setOpenPlanner] = useState(false as any);
  const filters = ["Price: Low to High", "Rating", "Availability", "Distance"];
  
  return (
    <motion.div 
      className="space-y-6 animate-fade-in"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <motion.div 
        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground break-words">
            Best matches for "{searchTerm}"
          </h2>
          <p className="text-muted-foreground break-words">{companies.length} companies available</p>
        </div>
        
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto sm:justify-end">
          <Button variant="outline" size="sm" className="w-full sm:w-auto">
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button size="sm" onClick={() => setOpenPlanner(true)} className="w-full sm:w-auto">
            Plan Multiple Services
          </Button>
        </div>
      </motion.div>

      {/* Quick Filters */}
      <motion.div 
        className="flex flex-wrap gap-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {filters.map((filter, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
              {filter}
            </Badge>
          </motion.div>
        ))}
      </motion.div>

      {/* Results Grid */}
      {companies.length > 0 ? (
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {companies.map((company, index) => (
            <motion.div 
              key={company.id} 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              whileHover={{ y: -5 }}
              className="animate-slide-up"
            >
              <ServiceCard company={company} onSelect={onCompanySelect} />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div 
          className="text-center py-12"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="text-gray-500 mb-4">
            <SlidersHorizontal className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Searching for services...</h3>
            <p className="text-gray-600">We're finding the best cleaning professionals for your needs.</p>
          </div>
        </motion.div>
      )}

      <MultiServiceScheduler open={openPlanner} onOpenChange={setOpenPlanner} onConfirm={() => {}} />
    </motion.div>
  );
};